import type {
  ApplicationStatus,
  EmployerApplicantCandidateSummary,
  EmployerApplicantCvSnapshot,
  EmployerApplicantDetail,
  SubmissionReceiptFields,
} from "@tabbio/types";

/**
 * Company-wide Applications inbox contract (Employer Phase 1, PRD §9 / WP4).
 *
 * LOCAL, PINNED copy of the backend contract for the aggregate endpoint
 * (`GET /api/jobs/company/applications`) and the talent-submission detail
 * (`GET /api/jobs/company/talent-submissions/:submissionId`). The backend for
 * these lands after this frontend; the orchestrator absorbs these types into
 * `@tabbio/types` once both sides are merged. Do not import from here outside
 * the applications inbox surfaces.
 */

export type CompanyApplicationKind = "application" | "talent";

export type CompanyApplicationsSort = "activity" | "newest";

/**
 * Talent rows report `active` while the candidate is in the network; the
 * application ladder statuses belong to real job applications. `withdrawn`
 * exists on both sides.
 */
export type CompanyApplicationItemStatus = ApplicationStatus | "active";

export interface CompanyApplicationDestination {
  type: "job" | "talent_network";
  jobId?: string | null;
  title: string;
}

export interface CompanyApplicationCandidate {
  name: string;
  avatarUrl?: string | null;
  headline?: string | null;
  location?: string | null;
}

export interface CompanyApplicationListItem {
  id: string;
  kind: CompanyApplicationKind;
  applicationId?: string | null;
  submissionId?: string | null;
  jobId?: string | null;
  destination: CompanyApplicationDestination;
  candidate: CompanyApplicationCandidate;
  status: CompanyApplicationItemStatus;
  lastActivityAt: string;
  submittedAt: string;
}

export interface CompanyApplicationListResult {
  items: CompanyApplicationListItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** `destinations` entries are job ids or the literal `talent_network`. */
export const TALENT_NETWORK_DESTINATION = "talent_network" as const;

export interface FetchCompanyApplicationsParams {
  query?: string;
  destinations?: string[];
  statuses?: ApplicationStatus[];
  sort?: CompanyApplicationsSort;
  page?: number;
  pageSize?: number;
}

// ─── Talent submission detail ───────────────────────────────────────────────

export type TalentSubmissionStatus = "active" | "withdrawn";

export interface TalentSubmissionConsent {
  version?: string | null;
  acceptedAt?: string | null;
  summary?: string | null;
}

export interface TalentSubmissionStatusHistoryEntry {
  status: string;
  at: string;
  byUserId?: string | null;
}

/**
 * The submitted CV snapshot carries `snapshot: true` when it is the immutable
 * submitted version; `snapshot: false` marks a live-CV fallback (label it
 * "Current CV", never "Submitted CV").
 */
export type TalentSubmissionCvDocument = EmployerApplicantCvSnapshot & {
  snapshot?: boolean;
};

/**
 * `receipts` / `receiptsStatus` (optional, audit E02): every submission of the
 * membership, newest first, the current one marked; a withdrawn and resubmitted
 * membership lists both. Rendered as summaries by `SubmissionReceiptsSection`
 * (audit E03).
 */
export interface TalentSubmissionDetail extends SubmissionReceiptFields {
  kind: "talent";
  submissionId: string;
  companyId: string;
  status: TalentSubmissionStatus;
  submittedAt: string;
  updatedAt: string;
  withdrawnAt?: string | null;
  consent?: TalentSubmissionConsent | null;
  candidate: EmployerApplicantCandidateSummary;
  candidateProfile?: Partial<
    EmployerApplicantDetail["candidateProfile"]
  > | null;
  submittedCvDocument: TalentSubmissionCvDocument | null;
  statusHistory: TalentSubmissionStatusHistoryEntry[];
}

// ─── Deep-link codecs ───────────────────────────────────────────────────────
//
// One scheme for both surfaces, so links copy across platforms:
//   native route:  /(employer)/applications/a_<applicationId>?jobId=<jobId>
//                  /(employer)/applications/t_<submissionId>
//   web selection: ?application=a:<applicationId>:<jobId>
//                  ?application=t:<submissionId>
// The application detail endpoint is per-job, so application links must carry
// the jobId; ids are cuids (no `:` or `_` prefixes collide).

export interface ApplicationItemRef {
  kind: CompanyApplicationKind;
  applicationId?: string;
  submissionId?: string;
  jobId?: string | null;
}

export function buildApplicationItemId(item: {
  kind: CompanyApplicationKind;
  applicationId?: string | null;
  submissionId?: string | null;
}): string | null {
  if (item.kind === "application" && item.applicationId) {
    return `a_${item.applicationId}`;
  }
  if (item.kind === "talent" && item.submissionId) {
    return `t_${item.submissionId}`;
  }
  return null;
}

export function parseApplicationItemId(
  itemId: string | null | undefined,
  jobId?: string | null,
): ApplicationItemRef | null {
  if (!itemId) return null;
  if (itemId.startsWith("a_") && itemId.length > 2) {
    return {
      kind: "application",
      applicationId: itemId.slice(2),
      jobId: jobId ?? null,
    };
  }
  if (itemId.startsWith("t_") && itemId.length > 2) {
    return { kind: "talent", submissionId: itemId.slice(2) };
  }
  return null;
}

export function buildApplicationSelectionParam(item: {
  kind: CompanyApplicationKind;
  applicationId?: string | null;
  submissionId?: string | null;
  jobId?: string | null;
}): string | null {
  if (item.kind === "application" && item.applicationId && item.jobId) {
    return `a:${item.applicationId}:${item.jobId}`;
  }
  if (item.kind === "talent" && item.submissionId) {
    return `t:${item.submissionId}`;
  }
  return null;
}

export function parseApplicationSelectionParam(
  value: string | null | undefined,
): ApplicationItemRef | null {
  if (!value) return null;
  const parts = value.split(":");
  if (parts[0] === "a" && parts[1] && parts[2]) {
    return { kind: "application", applicationId: parts[1], jobId: parts[2] };
  }
  if (parts[0] === "t" && parts[1]) {
    return { kind: "talent", submissionId: parts[1] };
  }
  return null;
}
